import { type ReactNode, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Brain, Copy, KeyRound, Link2, Pause, Play, RefreshCw } from 'lucide-react';
import { Badge, Button, Card, CardTitle, EmptyState } from '@/components/ui';
import { api } from '@/lib/api/client';
import { isApiError, unwrap } from '@/lib/api/envelope';
import type { ResponseEnvelope } from '@/types/api';
import { Endpoints } from '@/lib/api/endpoints';
import { useAuth } from '@/lib/auth/useAuth';
import {
  fetchClient,
  fetchClientPricing,
  registerClient,
  setClientPricing,
  setBalanceTransfer,
  suspendClient,
  reactivateClient,
  setBalance,
  addBalance,
  changeBalanceExpiry,
  rotateClientKey,
  type ClientDto,
  type ClientStatus,
  type ServicePricing,
} from './api';

const STATUS_TONE: Record<ClientStatus, 'success' | 'warning' | 'danger' | 'neutral' | 'primary'> =
  {
    ACTIVE:     'success',
    ONBOARDING: 'primary',
    INACTIVE:   'warning',
    SUSPENDED:  'danger',
    CANCELLED:  'neutral',
    REVOKED:    'danger',
  };

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { hasAnyRole } = useAuth();

  const isAdmin = hasAnyRole(['AUTHMATECH_SUPER_ADMIN', 'AUTHMATECH_ADMIN']);
  const isFinance = hasAnyRole(['AUTHMATECH_SUPER_ADMIN', 'AUTHMATECH_ADMIN', 'AUTHMATECH_FINANCE']);
  const canWrite = hasAnyRole([
    'AUTHMATECH_SUPER_ADMIN',
    'AUTHMATECH_ADMIN',
    'AUTHMATECH_OPERATOR',
  ]);

  const query = useQuery({
    queryKey: ['dashboard', 'client', id],
    queryFn: () => fetchClient(id!),
    enabled: !!id,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['dashboard', 'client', id] });

  const registerMutation = useMutation({
    mutationFn: () => registerClient(id!),
    onSuccess: (result) => {
      setRevealed({ apiKey: result.apiKey, sdkToken: result.sdkToken, clientSecret: result.clientSecret });
      invalidate();
    },
  });

  const suspendMutation = useMutation({
    mutationFn: () => suspendClient(id!),
    onSuccess: invalidate,
  });

  const reactivateMutation = useMutation({
    mutationFn: () => reactivateClient(id!),
    onSuccess: invalidate,
  });

  const rotateMutation = useMutation({
    mutationFn: () => rotateClientKey(id!),
    onSuccess: (result) => {
      setRevealed({ apiKey: result.apiKey, sdkToken: result.sdkToken, clientSecret: result.clientSecret });
      invalidate();
    },
  });

  const [revealed, setRevealed] = useState<{ apiKey?: string; sdkToken?: string; clientSecret?: string } | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [balanceForm, setBalanceForm] = useState({ amount: '', expiry: '' });
  const [pricingForm, setPricingForm] = useState({ serviceType: 'VERIFY_N', deductionAmount: '1', active: true });
  const [balanceSuccess, setBalanceSuccess] = useState<string | null>(null);
  const [pricingSuccess, setPricingSuccess] = useState(false);

  function showBalanceSuccess(msg: string) {
    setBalanceSuccess(msg);
    setTimeout(() => setBalanceSuccess(null), 4000);
  }

  const pricingQuery = useQuery({
    queryKey: ['dashboard', 'client', id, 'pricing'],
    queryFn: () => fetchClientPricing(id!),
    enabled: !!id && isFinance,
  });

  const setPricingMutation = useMutation({
    mutationFn: () =>
      setClientPricing(id!, {
        serviceType: pricingForm.serviceType,
        deductionAmount: parseInt(pricingForm.deductionAmount, 10),
        active: pricingForm.active,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboard', 'client', id, 'pricing'] });
      setPricingSuccess(true);
      setTimeout(() => setPricingSuccess(false), 4000);
    },
  });

  const setBalanceTransferMutation = useMutation({
    mutationFn: (enabled: boolean) => setBalanceTransfer(id!, enabled),
    onSuccess: invalidate,
  });

  const setBalanceMutation = useMutation({
    mutationFn: () =>
      setBalance(id!, {
        balance: parseInt(balanceForm.amount, 10),
        balanceExpiryDate: balanceForm.expiry,
      }),
    onSuccess: () => {
      setBalanceForm({ amount: '', expiry: '' });
      invalidate();
      showBalanceSuccess('Balance set successfully.');
    },
  });

  const addBalanceMutation = useMutation({
    mutationFn: () =>
      addBalance(id!, {
        amount: parseInt(balanceForm.amount, 10),
      }),
    onSuccess: () => {
      setBalanceForm({ amount: '', expiry: '' });
      invalidate();
      showBalanceSuccess('Credits added successfully.');
    },
  });

  const expiryOnlyMutation = useMutation({
    mutationFn: () =>
      changeBalanceExpiry(id!, { balanceExpiryDate: balanceForm.expiry }),
    onSuccess: () => {
      setBalanceForm((f) => ({ ...f, expiry: '' }));
      invalidate();
      showBalanceSuccess('Expiry date updated successfully.');
    },
  });

  function copyField(field: string, value: string) {
    navigator.clipboard.writeText(value).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    });
  }

  if (query.isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-5 w-24 animate-pulse rounded bg-surface-subtle" />
        <div className="h-7 w-48 animate-pulse rounded bg-surface-subtle" />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-40 animate-pulse rounded-xl bg-surface-subtle" />
          ))}
        </div>
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <EmptyState
        message={
          isApiError(query.error)
            ? query.error.firstErrorMessage
            : 'Could not load client.'
        }
      />
    );
  }

  const client: ClientDto = query.data;
  const expiryDate = client.balanceExpiryDate ? new Date(client.balanceExpiryDate) : null;
  const isExpired = expiryDate != null && expiryDate < new Date();
  const daysLeft = client.daysUntilBalanceExpiry;
  const isLowBalance = client.balance != null && client.balance > 0 && client.balance <= 500;

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <button
          onClick={() => navigate('/clients')}
          className="mb-3 inline-flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to clients
        </button>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold">{client.clientName}</h1>
            <p className="mt-0.5 font-mono text-xs text-fg-subtle">{client.id}</p>
          </div>
          <Badge tone={STATUS_TONE[client.clientStatus]}>{client.clientStatus}</Badge>
        </div>
      </div>

      {/* Revealed credentials — shown once after registration or rotation */}
      {revealed && (
        <div className="space-y-3">
          <div className="rounded-lg border border-amber-500/40 bg-amber-50 p-4 dark:bg-amber-500/10">
            <p className="mb-1 text-sm font-semibold text-amber-800 dark:text-amber-300">
              Credentials generated — copy each value now. They will not be shown again.
            </p>
            <p className="mb-3 text-xs text-amber-700 dark:text-amber-400">
              Store the Client Secret securely — it is required to rotate the API key or SDK token.
            </p>
            <div className="space-y-3">
              {revealed.apiKey && (
                <RevealedRow
                  label="Server API Key"
                  badge="Backend only"
                  value={revealed.apiKey}
                  field="apiKey"
                  copiedField={copiedField}
                  onCopy={copyField}
                />
              )}
              {revealed.sdkToken && (
                <RevealedRow
                  label="Mobile SDK Token"
                  badge="Mobile SDK only"
                  value={revealed.sdkToken}
                  field="sdkToken"
                  copiedField={copiedField}
                  onCopy={copyField}
                />
              )}
              {revealed.clientSecret && (
                <RevealedRow
                  label="Client Secret"
                  badge="Sensitive"
                  value={revealed.clientSecret}
                  field="clientSecret"
                  copiedField={copiedField}
                  onCopy={copyField}
                />
              )}
            </div>
            <button
              className="mt-3 text-xs text-amber-700 underline dark:text-amber-400"
              onClick={() => setRevealed(null)}
            >
              I've copied all credentials — dismiss
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Details */}
        <Card>
          <CardTitle className="mb-4">Details</CardTitle>
          <dl className="space-y-3 text-sm">
            <Row label="Contact" value={client.contactPersonName} />
            {client.contactPersonEmail && (
              <Row label="Email" value={client.contactPersonEmail} />
            )}
            <Row
              label="Status"
              value={<Badge tone={STATUS_TONE[client.clientStatus]}>{client.clientStatus}</Badge>}
            />
            {client.createdAt && (
              <Row label="Created" value={new Date(client.createdAt).toLocaleString()} />
            )}
          </dl>
        </Card>

        {/* Client actions */}
        {canWrite && (
          <Card>
            <CardTitle className="mb-1">Actions</CardTitle>
            {client.clientStatus === 'SUSPENDED' && (
              <p className="mb-3 text-xs text-fg-muted">
                This client is suspended. Verification traffic is blocked. Reactivate to resume service.
              </p>
            )}
            {client.clientStatus === 'ACTIVE' && isAdmin && (
              <p className="mb-3 text-xs text-fg-muted">
                Suspending a client stops verification traffic without deleting configuration or API credentials.
              </p>
            )}
            <div className="space-y-3">
              {client.clientStatus === 'ONBOARDING' && (
                <ActionButton
                  icon={<KeyRound className="h-4 w-4" />}
                  label="Activate & generate credentials"
                  description="Flips status to ACTIVE and issues the server API key, mobile SDK token, and client secret."
                  isLoading={registerMutation.isPending}
                  error={registerMutation.error}
                  onClick={() => registerMutation.mutate()}
                />
              )}
              {client.clientStatus === 'ACTIVE' && isAdmin && (
                <>
                  <ActionButton
                    icon={<RefreshCw className="h-4 w-4" />}
                    label="Rotate API key"
                    description="Immediately invalidates the current key and issues a new one."
                    isLoading={rotateMutation.isPending}
                    error={rotateMutation.error}
                    onClick={() => {
                      if (confirm(`Rotate API key for ${client.clientName}? The current key will stop working immediately.`)) {
                        rotateMutation.mutate();
                      }
                    }}
                  />
                  <ActionButton
                    icon={<Pause className="h-4 w-4" />}
                    label="Suspend client"
                    description="Blocks all verification requests. Config and credentials are preserved."
                    variant="danger"
                    isLoading={suspendMutation.isPending}
                    error={suspendMutation.error}
                    onClick={() => {
                      if (confirm(`Suspend ${client.clientName}? All verification requests will be blocked until reactivated.`)) {
                        suspendMutation.mutate();
                      }
                    }}
                  />
                </>
              )}
              {client.clientStatus === 'SUSPENDED' && isAdmin && (
                <ActionButton
                  icon={<Play className="h-4 w-4" />}
                  label="Reactivate client"
                  description="Restores ACTIVE status and resumes verification traffic."
                  isLoading={reactivateMutation.isPending}
                  error={reactivateMutation.error}
                  onClick={() => {
                    if (confirm(`Reactivate ${client.clientName}? Verification traffic will resume immediately.`)) {
                      reactivateMutation.mutate();
                    }
                  }}
                />
              )}
            </div>
          </Card>
        )}
      </div>

      {/* Current package / balance card */}
      <Card>
        <CardTitle className="mb-4">Current Package</CardTitle>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          <PackageStat
            label="Remaining balance"
            value={
              client.balance != null ? (
                <span className={isLowBalance ? 'text-amber-600 dark:text-amber-400' : ''}>
                  {client.balance.toLocaleString()}
                  {isLowBalance && <span className="ml-1.5 text-xs">⚠ Low</span>}
                </span>
              ) : '—'
            }
          />
          <PackageStat
            label="Expiry date"
            value={
              expiryDate ? (
                <span className={isExpired ? 'text-rose-600 dark:text-rose-400' : ''}>
                  {expiryDate.toLocaleDateString()}
                  {isExpired && ' · Expired'}
                </span>
              ) : '—'
            }
          />
          <PackageStat
            label="Days remaining"
            value={
              daysLeft != null && daysLeft > 0 ? (
                <span className={daysLeft <= 14 ? 'text-amber-600 dark:text-amber-400' : ''}>
                  {daysLeft}
                </span>
              ) : isExpired ? (
                <span className="text-rose-600 dark:text-rose-400">Expired</span>
              ) : '—'
            }
          />
          <PackageStat
            label="Balance status"
            value={
              isExpired ? (
                <Badge tone="danger">Expired</Badge>
              ) : client.balanceValid ? (
                <Badge tone="success">Active</Badge>
              ) : (
                <Badge tone="neutral">—</Badge>
              )
            }
          />
        </div>
      </Card>

      {/* Balance management */}
      {isFinance && (
        <Card>
          <div className="mb-4 flex items-start justify-between gap-4">
            <CardTitle>Balance Management</CardTitle>
            {isAdmin && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-fg-muted">Carry-over on renewal</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={client.transferExpiredBalance}
                  onClick={() => {
                    const next = !client.transferExpiredBalance;
                    if (confirm(
                      next
                        ? `Enable balance carry-over for ${client.clientName}?\n\nWhen a new package is set before the current expiry, the remaining balance will be added on top of the new starting balance.`
                        : `Disable balance carry-over for ${client.clientName}?\n\nWhen a new package is set, the existing balance will be discarded.`
                    )) {
                      setBalanceTransferMutation.mutate(next);
                    }
                  }}
                  disabled={setBalanceTransferMutation.isPending}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 ${
                    client.transferExpiredBalance ? 'bg-primary-500' : 'bg-border'
                  }`}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                      client.transferExpiredBalance ? 'translate-x-4' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
            )}
          </div>
          {client.transferExpiredBalance && (
            <div className="mb-4 rounded-lg border border-primary-200 bg-primary-50 px-3 py-2 text-xs text-primary-700 dark:border-primary-500/20 dark:bg-primary-500/10 dark:text-primary-300">
              Carry-over enabled — remaining credits will be added to the new balance on renewal.
            </div>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-fg-muted">
                Amount (verifications)
              </label>
              <input
                type="number"
                min="1"
                placeholder="e.g. 10 000"
                value={balanceForm.amount}
                onChange={(e) => setBalanceForm((f) => ({ ...f, amount: e.target.value }))}
                className="h-10 w-full rounded-lg border border-border bg-surface px-3 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-fg-muted">
                Expiry date
              </label>
              <input
                type="date"
                value={balanceForm.expiry}
                onChange={(e) => setBalanceForm((f) => ({ ...f, expiry: e.target.value }))}
                className="h-10 w-full rounded-lg border border-border bg-surface px-3 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
              />
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <Button
              size="sm"
              isLoading={setBalanceMutation.isPending}
              disabled={!balanceForm.amount || !balanceForm.expiry}
              onClick={() => {
                const current = client.balance ?? 0;
                const action = client.transferExpiredBalance && current > 0
                  ? `replace the current balance (${current.toLocaleString()} remaining) with ${parseInt(balanceForm.amount || '0', 10).toLocaleString()} credits + ${current.toLocaleString()} carried over`
                  : `replace the current balance (${current.toLocaleString()} remaining) with ${parseInt(balanceForm.amount || '0', 10).toLocaleString()} credits`;
                if (confirm(`Set balance for ${client.clientName}?\n\nThis will ${action}.`)) {
                  setBalanceMutation.mutate();
                }
              }}
            >
              Set balance
            </Button>
            <Button
              size="sm"
              variant="outline"
              isLoading={addBalanceMutation.isPending}
              disabled={!balanceForm.amount}
              onClick={() => addBalanceMutation.mutate()}
            >
              Add to balance
            </Button>
            <Button
              size="sm"
              variant="outline"
              isLoading={expiryOnlyMutation.isPending}
              disabled={!balanceForm.expiry}
              onClick={() => expiryOnlyMutation.mutate()}
            >
              Extend expiry date
            </Button>
          </div>
          {balanceSuccess && (
            <p className="mt-3 text-sm font-medium text-emerald-600 dark:text-emerald-400">
              {balanceSuccess}
            </p>
          )}
          {(setBalanceMutation.error != null ||
            addBalanceMutation.error != null ||
            expiryOnlyMutation.error != null ||
            setBalanceTransferMutation.error != null) && (
            <p className="mt-2 text-sm text-rose-600 dark:text-rose-400">
              {(() => {
                const err = setBalanceMutation.error ?? addBalanceMutation.error ?? expiryOnlyMutation.error ?? setBalanceTransferMutation.error;
                return isApiError(err) ? err.firstErrorMessage : 'Failed to update balance.';
              })()}
            </p>
          )}
        </Card>
      )}

      {/* Service Pricing Config */}
      {isFinance && (
        <Card>
          <CardTitle className="mb-4">Service Pricing</CardTitle>
          <p className="mb-4 text-xs text-fg-muted">
            Configure how many credits to deduct per API call. Defaults to 1 if not configured.
          </p>

          {pricingQuery.data && pricingQuery.data.length > 0 && (
            <div className="mb-4 overflow-hidden rounded-lg border border-border">
              <table className="w-full text-xs">
                <thead className="bg-surface-subtle">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-fg-muted">Service Type</th>
                    <th className="px-3 py-2 text-right font-medium text-fg-muted">Deduction</th>
                    <th className="px-3 py-2 text-center font-medium text-fg-muted">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {pricingQuery.data.map((p: ServicePricing) => (
                    <tr key={p.serviceType} className="border-t border-border">
                      <td className="px-3 py-2 font-medium text-fg">{p.serviceType}</td>
                      <td className="px-3 py-2 text-right text-fg">{p.deductionAmount} credit{p.deductionAmount !== 1 ? 's' : ''}</td>
                      <td className="px-3 py-2 text-center">
                        <Badge tone={p.active ? 'success' : 'neutral'}>{p.active ? 'Active' : 'Inactive'}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-fg-muted">Service Type</label>
              <select
                value={pricingForm.serviceType}
                onChange={(e) => setPricingForm((f) => ({ ...f, serviceType: e.target.value }))}
                className="h-10 w-full rounded-lg border border-border bg-surface px-3 text-sm outline-none focus:border-primary-500"
              >
                {['VERIFY_N', 'VERIFY_N_PLUS', 'OPPORTUNITY'].map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-fg-muted">Credits per call</label>
              <input
                type="number" min="1"
                value={pricingForm.deductionAmount}
                onChange={(e) => setPricingForm((f) => ({ ...f, deductionAmount: e.target.value }))}
                className="h-10 w-full rounded-lg border border-border bg-surface px-3 text-sm outline-none focus:border-primary-500"
              />
            </div>
            <div className="flex items-end">
              <Button
                size="sm"
                isLoading={setPricingMutation.isPending}
                disabled={!pricingForm.deductionAmount || parseInt(pricingForm.deductionAmount, 10) < 1}
                onClick={() => setPricingMutation.mutate()}
              >
                Save pricing
              </Button>
            </div>
          </div>
          {pricingSuccess && (
            <p className="mt-2 text-sm font-medium text-emerald-600 dark:text-emerald-400">
              Pricing saved.
            </p>
          )}
          {setPricingMutation.error != null && (
            <p className="mt-2 text-sm text-rose-600 dark:text-rose-400">
              {isApiError(setPricingMutation.error) ? setPricingMutation.error.firstErrorMessage : 'Failed to save pricing.'}
            </p>
          )}
        </Card>
      )}

      {/* ── Stuck+ Config ─────────────────────────────────────────────── */}
      {isAdmin && id && <StuckPlusConfigCard clientId={id} canWrite={canWrite} />}

      {/* ── Connect Config ────────────────────────────────────────────── */}
      {isAdmin && id && <ConnectConfigCard clientId={id} canWrite={canWrite} />}
    </div>
  );
}

interface StuckPlusConfigDto {
  clientId: string;
  enabled: boolean;
  revealEnabled: boolean;
  revealCredits: number;
  revealDurationSeconds?: number;
  requireRevealReason: boolean;
  maxRevealsPerDay?: number;
}

function StuckPlusConfigCard({ clientId, canWrite }: { clientId: string; canWrite: boolean }) {
  const qc = useQueryClient();
  const [saved, setSaved] = useState(false);

  const query = useQuery({
    queryKey: ['admin', 'stuck-plus-config', clientId],
    queryFn: async () =>
      unwrap(await api.get<ResponseEnvelope<StuckPlusConfigDto>>(Endpoints.adminStuckPlus.config(clientId))),
    staleTime: 60_000,
  });

  const [form, setForm] = useState<Partial<StuckPlusConfigDto>>({});
  const cfg: Partial<StuckPlusConfigDto> = { ...query.data, ...form };

  const saveMutation = useMutation({
    mutationFn: async () =>
      unwrap(await api.put<ResponseEnvelope<StuckPlusConfigDto>>(Endpoints.adminStuckPlus.config(clientId), cfg)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'stuck-plus-config', clientId] });
      setForm({});
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  return (
    <Card>
      <CardTitle className="mb-4">
        <span className="flex items-center gap-2">
          <Brain className="h-4 w-4 text-violet-400" />
          Stuck+ Configuration
        </span>
      </CardTitle>

      {query.isLoading ? (
        <p className="text-sm text-fg-muted">Loading…</p>
      ) : (
        <div className="space-y-3">
          <ToggleRow
            label="Stuck+ Enabled"
            checked={cfg.enabled ?? false}
            onChange={(v) => setForm((f) => ({ ...f, enabled: v }))}
            disabled={!canWrite}
          />
          <ToggleRow
            label="Secure Reveal Enabled"
            checked={cfg.revealEnabled ?? false}
            onChange={(v) => setForm((f) => ({ ...f, revealEnabled: v }))}
            disabled={!canWrite}
          />
          <ToggleRow
            label="Require Reveal Reason"
            checked={cfg.requireRevealReason ?? false}
            onChange={(v) => setForm((f) => ({ ...f, requireRevealReason: v }))}
            disabled={!canWrite}
          />
          <div className="grid grid-cols-3 gap-3">
            <NumField
              label="Reveal Credits"
              value={cfg.revealCredits ?? 5}
              onChange={(v) => setForm((f) => ({ ...f, revealCredits: v }))}
              disabled={!canWrite}
            />
            <NumField
              label="Duration (s)"
              value={cfg.revealDurationSeconds ?? 300}
              onChange={(v) => setForm((f) => ({ ...f, revealDurationSeconds: v }))}
              disabled={!canWrite}
            />
            <NumField
              label="Max Reveals/Day"
              value={cfg.maxRevealsPerDay ?? 10}
              onChange={(v) => setForm((f) => ({ ...f, maxRevealsPerDay: v }))}
              disabled={!canWrite}
            />
          </div>

          {canWrite && (
            <div className="flex items-center gap-3 pt-1">
              <Button size="sm" isLoading={saveMutation.isPending} onClick={() => saveMutation.mutate()}>
                Save
              </Button>
              {saved && <span className="text-xs text-emerald-400">Saved</span>}
              {saveMutation.error && (
                <span className="text-xs text-rose-400">
                  {isApiError(saveMutation.error) ? saveMutation.error.firstErrorMessage : 'Failed'}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

interface ConnectConfigDto {
  clientId: string;
  enabled: boolean;
  syncEnabled: boolean;
  enrichmentEnabled: boolean;
  enrichmentCredits: number;
  maxProfiles?: number;
  allowedProviders?: string[];
}

function ConnectConfigCard({ clientId, canWrite }: { clientId: string; canWrite: boolean }) {
  const qc = useQueryClient();
  const [saved, setSaved] = useState(false);

  const query = useQuery({
    queryKey: ['admin', 'connect-config', clientId],
    queryFn: async () =>
      unwrap(await api.get<ResponseEnvelope<ConnectConfigDto>>(Endpoints.adminConnect.config(clientId))),
    staleTime: 60_000,
  });

  const [form, setForm] = useState<Partial<ConnectConfigDto>>({});
  const cfg: Partial<ConnectConfigDto> = { ...query.data, ...form };

  const saveMutation = useMutation({
    mutationFn: async () =>
      unwrap(await api.put<ResponseEnvelope<ConnectConfigDto>>(Endpoints.adminConnect.config(clientId), cfg)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'connect-config', clientId] });
      setForm({});
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  return (
    <Card>
      <CardTitle className="mb-4">
        <span className="flex items-center gap-2">
          <Link2 className="h-4 w-4 text-blue-400" />
          Connect Configuration
        </span>
      </CardTitle>

      {query.isLoading ? (
        <p className="text-sm text-fg-muted">Loading…</p>
      ) : (
        <div className="space-y-3">
          <ToggleRow
            label="Connect Enabled"
            checked={cfg.enabled ?? false}
            onChange={(v) => setForm((f) => ({ ...f, enabled: v }))}
            disabled={!canWrite}
          />
          <ToggleRow
            label="Sync Enabled"
            checked={cfg.syncEnabled ?? false}
            onChange={(v) => setForm((f) => ({ ...f, syncEnabled: v }))}
            disabled={!canWrite}
          />
          <ToggleRow
            label="Enrichment Enabled"
            checked={cfg.enrichmentEnabled ?? false}
            onChange={(v) => setForm((f) => ({ ...f, enrichmentEnabled: v }))}
            disabled={!canWrite}
          />
          <div className="grid grid-cols-2 gap-3">
            <NumField
              label="Enrichment Credits"
              value={cfg.enrichmentCredits ?? 2}
              onChange={(v) => setForm((f) => ({ ...f, enrichmentCredits: v }))}
              disabled={!canWrite}
            />
            <NumField
              label="Max Profiles"
              value={cfg.maxProfiles ?? 10000}
              onChange={(v) => setForm((f) => ({ ...f, maxProfiles: v }))}
              disabled={!canWrite}
            />
          </div>

          {canWrite && (
            <div className="flex items-center gap-3 pt-1">
              <Button size="sm" isLoading={saveMutation.isPending} onClick={() => saveMutation.mutate()}>
                Save
              </Button>
              {saved && <span className="text-xs text-emerald-400">Saved</span>}
              {saveMutation.error && (
                <span className="text-xs text-rose-400">
                  {isApiError(saveMutation.error) ? saveMutation.error.firstErrorMessage : 'Failed'}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function ToggleRow({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-fg">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500/40 disabled:cursor-not-allowed disabled:opacity-50 ${
          checked ? 'bg-primary-600' : 'bg-surface-muted'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
}

function NumField({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  disabled?: boolean;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs text-fg-muted">{label}</label>
      <input
        type="number"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
        className="w-full rounded-lg border border-border bg-surface px-3 py-1.5 text-sm text-fg focus:border-primary-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-surface-muted"
      />
    </div>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-fg-muted">{label}</dt>
      <dd className="text-right font-medium">{value}</dd>
    </div>
  );
}

function PackageStat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-surface-subtle p-3">
      <p className="mb-1 text-xs text-fg-muted">{label}</p>
      <p className="text-sm font-semibold text-fg">{value}</p>
    </div>
  );
}

function RevealedRow({
  label,
  badge,
  value,
  field,
  copiedField,
  onCopy,
}: {
  label: string;
  badge: string;
  value: string;
  field: string;
  copiedField: string | null;
  onCopy: (field: string, value: string) => void;
}) {
  return (
    <div>
      <div className="mb-1 flex items-center gap-2">
        <span className="text-xs font-medium text-amber-800 dark:text-amber-300">{label}</span>
        <span className="rounded-full bg-amber-200/60 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-500/20 dark:text-amber-300">
          {badge}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <code className="flex-1 break-all rounded bg-white/70 px-3 py-2 text-xs font-mono text-amber-900 dark:bg-black/20 dark:text-amber-200">
          {value}
        </code>
        <Button variant="outline" size="sm" onClick={() => onCopy(field, value)}>
          <Copy className="h-3.5 w-3.5" />
          {copiedField === field ? 'Copied!' : 'Copy'}
        </Button>
      </div>
    </div>
  );
}

function ActionButton({
  icon,
  label,
  description,
  isLoading,
  error,
  onClick,
  variant = 'outline',
}: {
  icon: ReactNode;
  label: string;
  description: string;
  isLoading: boolean;
  error: unknown;
  onClick: () => void;
  variant?: 'outline' | 'danger';
}) {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium">{label}</p>
          <p className="text-xs text-fg-muted">{description}</p>
          {error != null && (
            <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">
              {isApiError(error) ? error.firstErrorMessage : 'Action failed'}
            </p>
          )}
        </div>
        <Button
          size="sm"
          variant={variant}
          isLoading={isLoading}
          onClick={onClick}
          className="shrink-0"
        >
          {icon}
          {label.split(' ')[0]}
        </Button>
      </div>
    </div>
  );
}
